/**
 * Live Ball game phase machine — runs continuously through:
 *
 *   initializing → (one-time sign-in)
 *   ↓
 *   betting (countdown 30s)
 *   ↓ (timer expires OR player presses PASANG)
 *   bettingClosed → waitingResult (POST /action/bet)
 *   ↓ (server returns ball number)
 *   drawing (machine.playDraw)
 *   ↓
 *   showResult (highlight + toast, hold ~5s)
 *   ↓
 *   newRound (brief pause) → betting
 *
 * If /action/bet fails or no bets were placed, we fall back to a mock draw so
 * the UI still cycles — the game itself stays watchable.
 */
import {
  BETTING_DURATION_MS,
  DEFAULT_AGENT_ID,
  DEFAULT_PASSWORD,
  NEW_ROUND_DELAY_MS,
  RESULT_HOLD_MS,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { LotteryMachine } from '../lib/lottery-machine';
import type { BetPanel } from '../ui/betPanel';
import type { Topbar } from '../ui/topbar';
import type { BetResult } from '../types/api';
import { CountdownTimer } from './countdownTimer';
import { displayedBalance } from './gameState';
import type { GameState } from './gameState';
import type { GameStateSnapshot } from './gameState';
import { MockResultSource } from './mockResults';
import type { UrlParams } from './urlParams';
import { getBet } from './variants';
import { debugLog, error, warn } from '../util/logger';

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  machine: LotteryMachine;
  panel: BetPanel;
  topbar: Topbar;
  params: UrlParams;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid = '';
  private timer = new CountdownTimer();
  private mock = new MockResultSource();
  private disposed = false;
  /** When true, /action/bet failed — keep cycling with mock results until the
   *  player refreshes. Lets the game UI stay alive on stage. */
  private mockMode = false;
  /**
   * Multiplayer mode — currently always false. Once the multiplayer hub spec
   * lands (server-pushed round phases, shared `gid`, broadcast countdown),
   * flip this on for those sessions and feed `timer` from the hub instead of
   * starting it locally.
   *
   * In single-player there is no countdown: the player picks their own pace
   * and ends the round by pressing PASANG.
   */
  private multiplayerMode = false;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.d.state.setPhase('initializing');
    this.d.topbar.setStatus('Connecting…');

    try {
      await this.signIn();
    } catch (e) {
      warn('[Orchestrator] sign-in failed — falling back to demo/mock mode:', e);
      this.mockMode = true;
      // Seed a demo balance so PASANG works.
      if (this.d.state.balance <= 0) this.d.state.setBalance(1_000_000);
      this.d.topbar.setStatus('Demo mode');
    }

    this.startNewRound();
  }

  // ---------------- Sign-in ----------------

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;
    const gameKey = this.d.state.variant.key;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] using token+gsid from URL');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.topbar.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');
      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.topbar.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey,
      });
      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;
      this.d.state.setBalance(session.balance);
      this.applyServerLimits(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.topbar.setStatus('Fetching balance…');
    try {
      const bal = await this.d.api.checkBalance({ sessionId: gsid, gameKey });
      this.d.state.setBalance(bal);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
    try {
      const info = await this.d.api.getInfo({ sessionId: gsid, gameKey });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerLimits(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing):', e);
    }
    this.d.topbar.setStatus('Ready');
  }

  /**
   * Merge server-supplied limits into game state. Skips fields that came back
   * as 0/empty so a partial reply doesn't clobber a previous good config.
   */
  private applyServerLimits(chips: number[], minBet: number, maxBet: number): void {
    const cur = this.d.state.config;
    const next = {
      chipValues: chips.length > 0 ? chips : [...cur.chipValues],
      minBet: minBet > 0 ? minBet : cur.minBet,
      maxBet: maxBet > 0 ? maxBet : cur.maxBet,
    };
    this.d.state.setConfig(next);
  }

  // ---------------- Phase flow ----------------

  private startNewRound(): void {
    if (this.disposed) return;
    this.d.state.resetForNewRound();
    this.d.panel.clearAllStakes();
    this.d.panel.clearHighlights();
    this.d.topbar.hideToast();
    this.d.topbar.hideResultBall();
    this.d.machine.reset();
    this.d.topbar.setRound(this.gid || '—');
    this.d.state.setPhase('betting');
    this.d.panel.setBettingEnabled(true);
    this.d.topbar.setStatus('Place your bets');

    if (this.multiplayerMode) {
      this.d.topbar.showCountdown();
      this.timer.start(BETTING_DURATION_MS, {
        onTick: (sec) => {
          this.d.state.setCountdown(sec);
          this.d.topbar.setCountdown(sec, 'betting');
        },
        onComplete: () => void this.closeBetting(/*viaTimer*/ true),
      });
    } else {
      // Single-player: no countdown — the round only closes when the player
      // presses PASANG. Hide the pill so the topbar doesn't show stale "--".
      this.d.topbar.hideCountdown();
    }
  }

  /** Player pressed PASANG. */
  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.bets.isEmpty) {
      this.d.topbar.setStatus('Pick a number first');
      return;
    }
    if (this.multiplayerMode) this.timer.stop();
    await this.closeBetting(false);
  }

  tryPlaceBet(betKey: string): void {
    if (this.d.state.phase !== 'betting') return;
    const chip = this.d.state.selectedChipValue;
    const ok = this.d.state.placeBet(betKey, chip);
    if (!ok) {
      this.d.topbar.setStatus('Bet rejected — check balance / limit');
    }
  }

  clearAll(): void {
    if (this.d.state.phase !== 'betting') return;
    this.d.state.clearBets();
    this.d.panel.clearAllStakes();
  }

  repeatLast(): void {
    if (this.d.state.phase !== 'betting') return;
    const ok = this.d.state.repeatBets();
    if (!ok) {
      this.d.topbar.setStatus('Nothing to repeat');
      return;
    }
    // Refresh stake labels for the repeated bets.
    const bets = this.d.state.bets;
    for (const k of bets.keys) this.d.panel.setStake(k, bets.amountFor(k));
  }

  selectChip(i: number): void {
    this.d.state.selectChip(i);
  }

  dispose(): void {
    this.disposed = true;
    this.timer.stop();
  }

  // ---------------- Round resolution ----------------

  private async closeBetting(_viaTimer: boolean): Promise<void> {
    const variant = this.d.state.variant;
    this.d.state.setPhase('bettingClosed');
    this.d.panel.setBettingEnabled(false);
    this.d.topbar.setCountdown(0, 'idle');
    this.d.topbar.setStatus('No more bets');

    const lines = this.d.state.bets.toBetLines((key) => getBet(variant, key));

    // Remember the bets so ULANG (next round) can replay them. Runs for both
    // mock and API paths — but only when something was actually placed. (The
    // mock branch used to skip this, which silently disabled ULANG in demo
    // mode.)
    if (lines.length > 0) this.d.state.storeAsLastConfirmed();

    // If no bet was placed (or we lost the API earlier), just mock-draw.
    if (this.mockMode || lines.length === 0) {
      const fake = this.mock.fakeResult(variant, lines);
      // Apply credit/debit ourselves in mock mode.
      const newBal = Math.max(0, this.d.state.balance + fake.totalWinLose);
      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: fake.nextRoundId,
        result: fake.ballNumber,
        totalWinLose: fake.totalWinLose,
        totalBet: this.d.state.bets.total,
        positiveWinAmount: fake.positiveWinAmount,
        hasPositiveWin: fake.hasPositiveWin,
      });
      this.gid = fake.nextRoundId;
      // Balance update is deferred until inside runDraw, so the wallet only
      // ticks when the ball lands and the win/lose toast appears.
      await this.runDraw(fake.ballNumber, fake.totalWinLose, newBal);
      this.scheduleNextRound();
      return;
    }

    this.d.state.setPhase('waitingResult');
    let result: BetResult;
    try {
      result = await this.d.api.placeBet({
        sessionId: this.gid,
        bets: lines,
        gid: this.gid,
        gameKey: variant.key,
      });
    } catch (e) {
      error('[Orchestrator] placeBet failed → switching to mock:', e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
        return;
      }
      this.mockMode = true;
      const fake = this.mock.fakeResult(variant, lines);
      const newBal = Math.max(0, this.d.state.balance + fake.totalWinLose);
      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: fake.nextRoundId,
        result: fake.ballNumber,
        totalWinLose: fake.totalWinLose,
        totalBet: this.d.state.bets.total,
        positiveWinAmount: fake.positiveWinAmount,
        hasPositiveWin: fake.hasPositiveWin,
      });
      this.gid = fake.nextRoundId;
      await this.runDraw(fake.ballNumber, fake.totalWinLose, newBal);
      this.scheduleNextRound();
      return;
    }

    this.d.state.applyRoundResult({
      roundId: this.gid,
      nextRoundId: result.nextRoundId,
      result: result.ballNumber,
      totalWinLose: result.totalWinLose,
      totalBet: this.d.state.bets.total,
      positiveWinAmount: result.positiveWinAmount,
      hasPositiveWin: result.hasPositiveWin,
    });
    this.gid = result.nextRoundId || this.gid;

    await this.runDraw(
      result.ballNumber,
      result.totalWinLose,
      result.afterBalance,
      result.winningBetIds,
    );
    this.scheduleNextRound();
  }

  private async runDraw(
    ballNumber: number,
    totalWinLose: number,
    /**
     * Wallet balance to commit AFTER the ball lands — keeps the BAL pill
     * unchanged during the spin so the player sees their stake "locked on
     * the table" until the result reveal.
     */
    newBalance: number,
    winningBetIds?: number[],
  ): Promise<void> {
    this.d.state.setPhase('drawing');
    this.d.topbar.setStatus('Drawing ball…');
    // Vendored LotteryMachine takes a result array; single-ball variants pass [n].
    await this.d.machine.playDraw([ballNumber]);

    // Ball is now in the selector — commit the balance change so the wallet
    // ticks in sync with the win/lose toast about to appear.
    this.d.state.setBalance(newBalance);

    this.d.state.setPhase('showResult');
    this.d.panel.highlightWinners(ballNumber, winningBetIds);

    const def = this.d.state.variant.bets.find((b) => b.winsOn.includes(ballNumber) && b.category === 'straight');
    const ballColor = def?.color ?? 'dark';
    this.d.topbar.showResultBall(ballNumber, ballColor);

    if (totalWinLose > 0) {
      this.d.topbar.showToast(`WIN  +${fmt(totalWinLose)}`, 'win');
      this.d.topbar.setStatus(`You win ${fmt(totalWinLose)}`);
    } else if (totalWinLose < 0) {
      this.d.topbar.showToast(`LOSE  ${fmt(totalWinLose)}`, 'lose');
      this.d.topbar.setStatus(`You lose ${fmt(-totalWinLose)}`);
    } else {
      this.d.topbar.showToast(`BALL ${ballNumber}`, 'info');
      this.d.topbar.setStatus('No bet placed');
    }

    await wait(RESULT_HOLD_MS);
  }

  private scheduleNextRound(): void {
    if (this.disposed) return;
    window.setTimeout(() => this.startNewRound(), NEW_ROUND_DELAY_MS);
  }

  // ---------------- State → UI sync ----------------

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    const nextDisp = displayedBalance(next);
    const prevDisp = displayedBalance(prev);
    if (nextDisp !== prevDisp) this.d.topbar.setBalance(nextDisp);
    if (next.bets !== prev.bets) {
      this.d.topbar.setBetTotal(next.bets.total);
      // Refresh stake on cells whose amount changed.
      const allKeys = new Set([...prev.bets.keys, ...next.bets.keys]);
      for (const k of allKeys) {
        this.d.panel.setStake(k, next.bets.amountFor(k));
      }
    }
    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.panel.setSelectedChip(next.selectedChipIndex);
    }
    if (next.config !== prev.config) {
      this.d.panel.bindChips(next.config);
      this.d.panel.setSelectedChip(next.selectedChipIndex);
    }
  }
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
function wait(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}
function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}
function parseGameUrl(currentGameUrl: string, loginToken: string): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const tk = u.searchParams.get('token');
    const gs = u.searchParams.get('gsid');
    return {
      gameToken: tk && tk.length > 0 ? tk : loginToken,
      gsid: gs ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}
